/**
 * Issue #79 (C1) — what the Dashboard's "add an activity" action offers,
 * and where each option lands.
 *
 * Martin, 2026-08-04: *"On Dashboard the Upcoming Activities have a link to
 * Schedule, which takes you to Log match screen — this needs the ability to
 * take you directly to uploading either a match, training or development."*
 * One affordance, one destination, and that destination was always the
 * match screen: a player who wanted to add a training session had to know
 * to go somewhere else entirely.
 *
 * The table lives here rather than inside the menu component for the same
 * reason `trainingStatus.js` and `sessionContext.js` do: the rule about
 * WHICH options a role is offered is the acceptance criterion (A1 — "a
 * coach must not be offered Match"), and it should be testable without
 * mounting a dropdown. This repo has no component-rendering test harness,
 * so a rule that only exists inside JSX is a rule that only e2e can check.
 *
 * Every destination carries `?new=1` (see `hooks/useCreateDialog.js`), so
 * each option opens the target screen's own create dialog rather than
 * dropping the player on the page to find the button themselves. That is
 * the ticket's point 2 — "prefer opening the target directly in create
 * mode" — done by the target page reading a param, not by the Dashboard
 * driving someone else's UI through a click-chain.
 */

/** The query param that asks a page to open its create dialog. */
export const CREATE_PARAM = "new";

/**
 * `?new=1` is a create request. Nothing else is — not `?new`, not
 * `?new=0`, not `?new=true`. One spelling, so a link that means "open the
 * dialog" is greppable and can't drift into three near-misses.
 */
export function wantsCreate(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return params?.get(CREATE_PARAM) === "1";
}

/**
 * Issue #766 ruling 4 (D167) — the second param `?new=1` gained: "Log a
 * match here" (`GroundCard.jsx`) pre-selects the ground it was opened
 * from, so the log flow's own ground step (`LogMatchGroundStep.jsx`) never
 * makes the player pick a ground they are already standing on the card
 * for. One added parameter, in this one place, rather than a second
 * query-string builder elsewhere — the exact "a redrawn screen asks a
 * helper, never hardcodes a cross-screen route" precedent
 * `GROUNDS_MAP_PATH` (`lib/groundsNearYou.js`) already set.
 */
export const GROUND_PARAM = "ground";

/**
 * PR #921 review (F1) — the id alone is not enough to RESOLVE the ground
 * on the other end without hitting the same 1,000-row cap the played/
 * nearby row already escapes: `LogMatchGroundStep.jsx` has no directory
 * listing of its own to search, so the coordinate and name that
 * `GroundCard.jsx` already has in hand travel in the link too, for
 * `lib/directoryLoad.js`'s `resolveGroundById` (a tight bbox first, a name
 * search if that comes up empty — see that function's own header). Both
 * are optional: a ground with no pin carries no coordinate, and
 * `resolveGroundById` falls back to the name alone.
 */
export const GROUND_LAT_PARAM = "glat";
export const GROUND_LNG_PARAM = "glng";
export const GROUND_NAME_PARAM = "gname";

/** `?ground=<id>` reader, `wantsCreate`'s own shape: absent, or blank, means
 * no ground was carried — never guessed at. */
export function wantsGroundId(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const id = params?.get(GROUND_PARAM);
  return id ? id : null;
}

/**
 * The whole carried hint, for `resolveGroundById` — `groundId: null` means
 * none of this was ever asked for, same "absent, never guessed at" rule
 * `wantsGroundId` states alone. `latitude`/`longitude` parse to `null`
 * (not `NaN`) on anything that is not a finite number, so a corrupted or
 * hand-edited URL degrades to the name-only fallback rather than feeding
 * a bbox request `NaN`.
 */
export function wantsGroundHint(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const groundId = wantsGroundId(params);
  if (!groundId) return { groundId: null, name: null, latitude: null, longitude: null };
  const lat = Number(params?.get(GROUND_LAT_PARAM));
  const lng = Number(params?.get(GROUND_LNG_PARAM));
  return {
    groundId,
    name: params?.get(GROUND_NAME_PARAM) || null,
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
  };
}

/**
 * Append `?new=1` to a path that may already carry a query string, and —
 * when `groundId` is supplied — `&ground=<id>` beside it (ruling 4),
 * plus `&glat=`/`&glng=`/`&gname=` for whichever of `latitude`/
 * `longitude`/`groundName` the caller actually has (a ground with no pin
 * carries no coordinate at all, never `0,0`).
 */
export function withCreate(path, { groundId, groundName, latitude, longitude } = {}) {
  const withNew = `${path}${path.includes("?") ? "&" : "?"}${CREATE_PARAM}=1`;
  if (!groundId) return withNew;
  let out = `${withNew}&${GROUND_PARAM}=${encodeURIComponent(groundId)}`;
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    out += `&${GROUND_LAT_PARAM}=${encodeURIComponent(latitude)}&${GROUND_LNG_PARAM}=${encodeURIComponent(longitude)}`;
  }
  if (groundName) out += `&${GROUND_NAME_PARAM}=${encodeURIComponent(groundName)}`;
  return out;
}

/**
 * The three destinations, and who is offered each.
 *
 * `roles` is per-option rather than a single "hide Match from a coach"
 * branch, because the coach case is not the only one and the others are
 * less obvious:
 *
 * - **match** — a player's own innings. `/performance` reads `GET /matches`
 *   for the caller's own `player_profiles` row, which is why
 *   `PerformanceTracking.jsx` already branches on `isCoach`. This is A1:
 *   a coach logs nobody's match, so a coach is never offered one.
 * - **training** — the one option a coach genuinely has (#75): a coach may
 *   schedule a session in a linked player's calendar, and
 *   `TrainingSessions.jsx` already renders their half of that form.
 * - **development** — player-only, and this is the non-obvious one.
 *   `/development` is a player's alone (#1166, D215.1: a coach is sent to
 *   the Coach Portal), and `GET /goals` with no `player_id` is a
 *   deterministic 400 for a coach caller (`api/src/routes/authz.ts:60`).
 *   A coach's goal-assignment surface is the Coach Portal, not this page,
 *   so offering a coach "Development" here would be an option that lands
 *   on a screen which cannot load. It is left out rather than pointed
 *   somewhere else: re-homing a coach's goal create is #82/#218 territory,
 *   not a routing change's to invent.
 *
 * NOTE ON WHERE THIS IS RENDERED TODAY: the Upcoming Activities widget is
 * on the PLAYER dashboard only — `Dashboard.jsx` early-returns
 * `CoachDashboard`/`AdminDashboard` before it, by design (#74). So the
 * role filter here is not what makes A1 true today; it is what keeps A1
 * true the day a coach-side upcoming widget arrives (#82), which is
 * precisely the seam `api/src/routes/activities.ts` already warns about.
 */
/**
 * Issue #1149 — Plan a match is a step of `/development`, the way
 * `?milestone=` is, not a route of its own. Declared here (not in
 * `comingUp.js`, which re-exports it) so the one table below can name it
 * without a circular import.
 */
export const PLAN_MATCH_PATH = "/development?plan=match";

const OPTIONS = [
  {
    key: "match",
    label: "Match",
    hint: "Log an innings",
    to: withCreate("/performance"),
    roles: ["player"],
    plan: { key: "match", label: "Match", hint: "Plan a match you're going to play", to: PLAN_MATCH_PATH },
  },
  {
    key: "training",
    label: "Training",
    hint: "Plan or capture a session",
    // B2 renames the nav label to "Coaching"; the ROUTE stays `/learn`, and
    // the training surface stays its `tab=training` panel until #218 moves
    // it. Anything here that guessed at the post-#218 path would be a link
    // to a page that does not exist yet.
    to: withCreate("/learn?tab=training"),
    roles: ["player", "coach"],
    plan: { key: "training", label: "Training", hint: "Plan a training session", to: withCreate("/learn?tab=training") },
  },
  {
    key: "development",
    label: "Development",
    hint: "Set a goal",
    to: withCreate("/development"),
    roles: ["player"],
    plan: { key: "goal", label: "Goals", hint: "Set a new goal", to: withCreate("/development") },
  },
];

/**
 * The options a role is offered, in menu order. An unknown/absent role gets
 * the coach-safe subset rather than everything: this runs before `user` has
 * resolved on a cold load, and briefly offering "Match" to someone who
 * turns out to be a coach is the exact thing A1 forbids.
 */
export function activityCreateOptions(role) {
  // Only a player has a `player_profiles` row, so only a player has an own
  // match or an own goal. Admin resolves to the coach subset for that
  // reason rather than by analogy — `GET /matches` and `GET /goals` fail
  // for an admin caller exactly as they do for a coach one — and an
  // unresolved role resolves there too, so nothing can flash "Match" at
  // someone who turns out not to be a player.
  const effective = role === "player" ? "player" : "coach";
  return OPTIONS.filter((o) => o.roles.includes(effective));
}

/**
 * Issue #1149 (D225 item 3) — Plan's one `Add` menu: Match, Training, Goals.
 * The SAME rows as `activityCreateOptions`, read through each row's `plan`
 * face, so the role rule (A1) lives once and #712's top-bar "+ New" menu
 * can reuse the table rather than keep a second list. Here Match PLANS a
 * match (the Plan a match step); on the Dashboard chooser it still logs one.
 */
export function planAddOptions(role) {
  return activityCreateOptions(role).map((o) => o.plan);
}
